import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, PenLine } from "lucide-react";
import { getInviteeByToken, signInvitee } from "@/lib/afmApi";
import { Logo } from "@/components/Logo";
import { SignaturePad } from "@/components/SignaturePad";
import { Loading, ErrorState } from "@/components/common";

const INK = "#171717", BG = "#FAFAFA", ACCENT = "#9E1B22";

export default function SignInvite() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [status, setStatus] = useState("loading");
  const [sig, setSig] = useState(null);
  const [signing, setSigning] = useState(false);

  const load = () => {
    setStatus("loading");
    getInviteeByToken(token)
      .then((d) => { setInv(d); setStatus("ok"); })
      .catch(() => setStatus("error"));
  };
  useEffect(load, [token]);

  const submit = async () => {
    if (!sig) { toast.error("Dibuja tu firma primero"); return; }
    setSigning(true);
    try {
      await signInvitee(token, { signature_data_url: sig });
      toast.success("¡Firma registrada!");
      setInv((p) => ({ ...p, status: "signed" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar la firma");
    } finally { setSigning(false); }
  };

  if (status === "loading") return <Loading />;
  if (status === "error" || !inv) return <ErrorState onRetry={load} />;

  const signed = inv.status === "signed";
  return (
    <div className="min-h-screen" style={{ backgroundColor: BG, color: INK }} data-testid="sign-invite-page">
      <header className="border-b" style={{ borderColor: `${INK}1f` }}>
        <div className="mx-auto flex max-w-2xl items-center px-5 py-4"><Link to="/"><Logo imgClassName="h-10 w-auto object-contain" /></Link></div>
      </header>
      <main className="mx-auto max-w-2xl px-5 py-14">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>Split Sheet · Firma de colaborador</div>
        <h1 className="mt-3 font-serif text-3xl font-bold tracking-tight lg:text-4xl">{inv.project_title || "Acuerdo de reparto"}</h1>
        <p className="mt-2 text-sm" style={{ color: `${INK}cc` }}>Hola {inv.name || inv.email}, se te asignó el siguiente reparto. Revísalo y firma para dejar constancia de tu aceptación.</p>

        <div className="mt-8 grid grid-cols-2 gap-4">
          <div className="rounded-2xl border bg-[#FFFFFF] p-5" style={{ borderColor: "#E5E5E5" }}>
            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: `${INK}99` }}>Máster</div>
            <div className="mt-1 font-serif text-4xl font-bold" style={{ color: ACCENT }} data-testid="invite-master">{inv.master_pct}%</div>
          </div>
          <div className="rounded-2xl border bg-[#FFFFFF] p-5" style={{ borderColor: "#E5E5E5" }}>
            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: `${INK}99` }}>Publishing</div>
            <div className="mt-1 font-serif text-4xl font-bold" style={{ color: ACCENT }} data-testid="invite-publishing">{inv.publishing_pct}%</div>
          </div>
        </div>

        {signed ? (
          <div className="mt-8 flex items-center gap-2 rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-sm font-semibold text-emerald-700" data-testid="invite-signed">
            <CheckCircle2 className="h-5 w-5" /> Ya firmaste este acuerdo. ¡Gracias! Puedes cerrar esta página.
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border bg-[#FFFFFF] p-6" style={{ borderColor: "#E5E5E5" }}>
            <div className="mb-3 flex items-center gap-2 font-serif text-lg font-bold"><PenLine className="h-5 w-5" /> Tu firma</div>
            <SignaturePad onChange={setSig} />
            <button onClick={submit} disabled={signing || !sig} data-testid="invite-sign-button" className="mt-4 rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.03] disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
              {signing ? "Firmando…" : "Firmar acuerdo"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
